import { useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Phase 4a — auth state in the web UI.
 *
 * Two parallel auth modes:
 *   1) Cookie session (preferred): set by `POST /v1/auth/login`. Detected
 *      by hitting `GET /v1/auth/me` which returns 200/401.
 *   2) Legacy bearer paste: a JWT obtained via curl on the OAuth token
 *      endpoint, stashed in localStorage. Useful when the operator wants
 *      to drive the UI as a machine OAuth client.
 *
 * The combined "is the user authenticated?" answer is cookie OR token.
 */
const STORAGE_KEY = 'xsc.token';

const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

export function setToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  if (token == null) {
    window.localStorage.removeItem(STORAGE_KEY);
  } else {
    window.localStorage.setItem(STORAGE_KEY, token);
  }
  notify();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  const onStorage = (e: StorageEvent): void => {
    if (e.key === STORAGE_KEY) callback();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(callback);
    window.removeEventListener('storage', onStorage);
  };
}

export function useToken(): string | null {
  return useSyncExternalStore(subscribe, getToken, () => null);
}

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'member' | 'viewer';
  customer_id: string;
}

export interface SessionInfo {
  source: 'cookie' | 'bearer';
  user?: SessionUser;
  client_id?: string;
  customer_id?: string;
  scope?: string;
}

const PATH_PREFIX = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? '';
const PATH_PREFIX_NORMALIZED = PATH_PREFIX.replace(/\/$/, '');
const BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? PATH_PREFIX_NORMALIZED;

/**
 * Reactively probe `/v1/auth/me`. Tracks both cookie sessions and bearer
 * tokens (the api-client sends Authorization on every fetch automatically).
 * 401 → null. Refetches on window focus and after explicit invalidation
 * (login/logout flows call queryClient.invalidateQueries(['auth','me'])).
 */
export function useSession(): {
  data: SessionInfo | null;
  isLoading: boolean;
} {
  const token = useToken();
  const q = useQuery<SessionInfo | null>({
    queryKey: ['auth', 'me', token ?? 'cookie'],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(BASE_URL + '/v1/auth/me', {
        credentials: 'include',
        headers,
      });
      if (res.status === 401) return null;
      if (!res.ok) throw new Error(`/v1/auth/me ${res.status}`);
      return (await res.json()) as SessionInfo;
    },
    staleTime: 30_000,
    retry: false,
  });
  return { data: q.data ?? null, isLoading: q.isLoading };
}

export function useIsAuthenticated(): boolean {
  const { data, isLoading } = useSession();
  // While the /v1/auth/me probe is in flight on a fresh page load, optimistically
  // treat the user as authenticated. If the probe ultimately returns 401, this
  // flips to false and RequireAuth bounces to /login then. Returning false during
  // loading caused a hard race after the Google OAuth callback redirect: the SPA
  // would mount, see no session yet, redirect to /login, and discard the valid
  // cookie session that the probe was about to confirm.
  if (data) return true;
  if (isLoading) return true;
  return false;
}

/**
 * Sign out. Clears the cookie via POST /v1/auth/logout AND drops the
 * legacy bearer from localStorage; then nukes all query cache so
 * subsequent re-renders fall through to /login.
 */
export function useLogout(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    try {
      await fetch(BASE_URL + '/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // best-effort; clear local state regardless
    }
    setToken(null);
    qc.removeQueries();
    notify();
  };
}
